import React, { useState, useCallback } from 'react';
import ProjectCard from '../components/ProjectCard';
import '../components/Dashboard.css';
import { useAuth } from '../context/AuthContext';

const SHARE_API_BASE = process.env.REACT_APP_SHARE_API_BASE || null;

export default function Dashboard() {
  const { user, projects } = useAuth();
  const [shareModal, setShareModal] = useState({
    open: false,
    projectId: null,
    projectName: null,
    members: [],
    loadingMembers: false,
    sharing: null,   // uid currently being shared to
    shared: {},      // uid → true for already-shared this session
    error: null,
  });

  const openShareModal = useCallback(async (projectId, projectName) => {
    if (!SHARE_API_BASE) {
      setShareModal(m => ({ ...m, open: true, projectId, projectName, members: [], loadingMembers: false, error: 'Share service is not configured yet. Deploy share-backend and set REACT_APP_SHARE_API_BASE.' }));
      return;
    }

    setShareModal(m => ({ ...m, open: true, projectId, projectName, members: [], loadingMembers: true, sharing: null, shared: {}, error: null }));

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${SHARE_API_BASE}/company-members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load members');
      setShareModal(m => ({ ...m, loadingMembers: false, members: data.members || [] }));
    } catch (err) {
      setShareModal(m => ({ ...m, loadingMembers: false, error: err.message }));
    }
  }, [user]);

  const handleShare = useCallback(async (targetUserId) => {
    if (!SHARE_API_BASE) return;
    setShareModal(m => ({ ...m, sharing: targetUserId, error: null }));

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${SHARE_API_BASE}/share-project`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: shareModal.projectId, targetUserId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to share');
      setShareModal(m => ({ ...m, sharing: null, shared: { ...m.shared, [targetUserId]: true } }));
    } catch (err) {
      setShareModal(m => ({ ...m, sharing: null, error: err.message }));
    }
  }, [user, shareModal.projectId]);

  const closeShareModal = () => setShareModal({ open: false, projectId: null, projectName: null, members: [], loadingMembers: false, sharing: null, shared: {}, error: null });

  return (
    <div className='dashboard-container'>
      <h2>Projects</h2>
      <div className="dashboard-stats">
        <div className="stat-card">
          <p className="stat-title">Active Projects</p>
          <p className="stat-value">{projects?.length || 0}</p>
        </div>
        <div className="stat-card">
          <p className="stat-title">Photos Uploaded Today</p>
          <p className="stat-value">x</p>
        </div>
        <div className="stat-card">
          <p className="stat-title">Pending Receipts</p>
          <p className="stat-value">y</p>
        </div>
        <div className="stat-card">
          <p className="stat-title">Deadlines This Week</p>
          <p className="stat-value">z</p>
        </div>
      </div>

      <div className='project-grid'>
        {projects?.length > 0 ? (
          projects.map((proj) => (
            <ProjectCard
              key={proj.id}
              project={proj}
              onShareClick={openShareModal}
            />
          ))
        ) : (
          <p>No projects yet.</p>
        )}
      </div>

      {shareModal.open && (
        <div className="share-modal-overlay" onClick={closeShareModal}>
          <div className="share-modal" onClick={e => e.stopPropagation()}>
            <button className="share-modal-close" onClick={closeShareModal}>✕</button>
            <h3 className="share-modal-title">Share Project</h3>
            <p className="share-modal-subtitle">{shareModal.projectName}</p>

            {shareModal.error && (
              <div className="share-modal-error">{shareModal.error}</div>
            )}

            {shareModal.loadingMembers ? (
              <div className="share-modal-loading">
                <div className="share-spinner" />
                <span>Loading team members…</span>
              </div>
            ) : shareModal.members.length === 0 && !shareModal.error ? (
              <p className="share-modal-empty">No other team members found in your company.</p>
            ) : (
              <ul className="share-member-list">
                {shareModal.members.map(member => {
                  const isSharing = shareModal.sharing === member.uid;
                  const isDone = shareModal.shared[member.uid];
                  const initials = (member.name || member.email || '?')
                    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

                  return (
                    <li key={member.uid} className="share-member-row">
                      <div className="share-member-avatar">{initials}</div>
                      <div className="share-member-info">
                        <span className="share-member-name">{member.name || member.email}</span>
                        {member.name && <span className="share-member-email">{member.email}</span>}
                      </div>
                      <button
                        className={`share-member-btn ${isDone ? 'share-member-btn--done' : ''}`}
                        onClick={() => !isDone && handleShare(member.uid)}
                        disabled={isSharing || isDone}
                      >
                        {isSharing ? '…' : isDone ? 'Shared ✓' : 'Share'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
